import type {
  Opportunity,
  StageTransition,
} from "@/hooks/api/use-account-opportunities";
import type { Person } from "@/hooks/api/use-account-persons";
import type { MilestoneAnalysis } from "@/hooks/api/use-milestone-analyses";
import {
  formatDayMonth,
  formatMonthYear,
  getTimestamp,
} from "@/lib/date-formatting";
import type { PersonScatterPoint } from "./person-avatar";
import { generateCustomTicks } from "./utils";

// -----------------------------------------------------------
// Minimap tick-generation constants
// -----------------------------------------------------------
const MIN_TICK_GAP_PX = 80; // Minimum horizontal space per label
const BASE_MAX_TICKS = 12; // Full timeline default
const CUSTOM_BASE_MAX_TICKS = 60; // Upper bound when zoomed
const PADDING_FACTOR = 1.2; // Matches calculateChartWidth padding

interface FirstTouchpointData {
  first_touchpoints: Array<{
    person_id: string;
    date: string;
  }>;
}

export interface VisibleTimeRange {
  startDate: Date;
  endDate: Date;
  selectedRange?: {
    start: Date;
    end: Date;
  };
}

interface TimelineData {
  activities: Array<{
    date: string;
    decayed_in_value: number;
    decayed_out_value: number;
  }>;
}

// Helper function to format date string consistently
const getDateString = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

export const formatActivityChartData = (
  timelineData: TimelineData | undefined,
  visibleTimeRange: VisibleTimeRange | null,
) => {
  if (!visibleTimeRange) return [];

  // First, get the activity data points we have
  const activityPoints =
    timelineData?.activities.map((activity) => ({
      ...activity,
      timestamp: getTimestamp(activity.date),
    })) ?? [];

  // Filter points within the visible range
  const filteredPoints = activityPoints.filter(
    (point) =>
      point.timestamp >= visibleTimeRange.startDate.getTime() &&
      point.timestamp <= visibleTimeRange.endDate.getTime(),
  );

  // If we have no data points, return empty array
  if (!filteredPoints.length) return [];

  // Create a Map indexed by date string for O(1) lookups instead of repeated find() operations
  const pointsByDate = new Map();
  filteredPoints.forEach((point) => {
    const dateString = getDateString(new Date(point.timestamp));
    pointsByDate.set(dateString, point);
  });

  // Generate daily points from start to end - use more efficient array initialization
  const startDate = new Date(visibleTimeRange.startDate);
  const endDate = new Date(visibleTimeRange.endDate);

  // Calculate total number of days to avoid dynamic array resizing
  const totalDays =
    Math.floor(
      (endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000),
    ) + 1;

  // Pre-allocate array of correct size
  const result = new Array(totalDays);

  // Fill array with data points
  const currentDate = new Date(startDate);
  for (let i = 0; i < totalDays; i++) {
    const timestamp = currentDate.getTime();
    const dateString = getDateString(currentDate);
    const existingPoint = pointsByDate.get(dateString);

    result[i] = {
      date: dateString,
      timestamp,
      decayed_in_value: existingPoint?.decayed_in_value ?? 0,
      ...existingPoint,
    };

    // Increment date more efficiently
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return result;
};

// Helper function to get current stage at a timestamp
function getCurrentStage(opp: Opportunity, timestamp: number): string {
  if (!opp.stage_transitions?.length) return opp.stage_name;

  const transition = opp.stage_transitions
    .filter((t) => getTimestamp(t.timestamp) <= timestamp)
    .sort((a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp))[0];

  return transition
    ? transition.event_name.replace("Changed stage to ", "")
    : opp.stage_name;
}

// Helper function to get outcome category
export function getOutcomeCategory(
  is_closed: boolean,
  is_won: boolean,
): "won" | "lost" | "active" {
  if (!is_closed) return "active";
  if (is_won) return "won";
  if (!is_won) return "lost";
  return "active";
}

export interface OpportunityDataPoint {
  timestamp: number;
  opportunities: Array<{
    id: string;
    name: string;
    stage: string;
    outcome: "won" | "lost" | "active";
    stageTransitions: StageTransition[];
    y: number;
  }>;
}

export const formatOpportunityData = (
  opportunities: Opportunity[] | undefined,
  visibleTimeRange: VisibleTimeRange | null,
): OpportunityDataPoint[] => {
  if (!opportunities?.length || !visibleTimeRange) return [];

  const { startDate, endDate } = visibleTimeRange;
  const dataPoints: OpportunityDataPoint[] = [];

  // First, assign a fixed vertical position to each opportunity
  // Reverse the index assignment so that later opportunities (higher indices) are at the bottom
  const opportunityPositions = opportunities.reduce(
    (acc, opp, index) => {
      // Reverse the index so that later opportunities are at the bottom
      acc[opp.id] = opportunities.length - 1 - index;
      return acc;
    },
    {} as Record<string, number>,
  );

  const totalOpportunities = opportunities.length;
  const heightPerOpportunity = 1 / (totalOpportunities + 1); // Add 1 for spacing

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const timestamp = d.getTime();

    // Map active opportunities to their current state
    const activeOpps = opportunities
      .map((opp) => {
        // Only include opportunities that exist at this timestamp
        const oppCreatedTime = getTimestamp(opp.created_date);
        const oppCloseTime = opp.close_date
          ? getTimestamp(opp.close_date)
          : Infinity;

        // Skip if the opportunity hasn't been created yet or is already closed
        if (timestamp < oppCreatedTime || timestamp > oppCloseTime) {
          return null;
        }

        return {
          id: opp.id,
          name: opp.name,
          stage: getCurrentStage(opp, timestamp),
          outcome: getOutcomeCategory(opp.is_closed, opp.is_won),
          stageTransitions: opp.stage_transitions || [],
          y: (opportunityPositions[opp.id] + 1) * heightPerOpportunity, // Add 1 to leave space at top
        };
      })
      .filter((opp): opp is NonNullable<typeof opp> => opp !== null);

    dataPoints.push({
      timestamp,
      opportunities: activeOpps,
    });
  }

  return dataPoints;
};

export const formatFirstTouchpoints = (
  firstTouchpointData: FirstTouchpointData | undefined,
  visibleTimeRange: VisibleTimeRange | null,
  getPersonById: (id: string) => Person | undefined,
  customTimeRange: { start: Date; end: Date } | null,
): PersonScatterPoint[] => {
  if (!firstTouchpointData?.first_touchpoints || !visibleTimeRange) return [];

  // Filter and sort touchpoints within the visible range
  const filteredTouchpoints = firstTouchpointData.first_touchpoints
    .map((touchpoint) => {
      const personData = getPersonById(touchpoint.person_id);
      if (!personData) return null;

      const touchpointTimestamp = getTimestamp(touchpoint.date);
      if (
        touchpointTimestamp < visibleTimeRange.startDate.getTime() ||
        touchpointTimestamp > visibleTimeRange.endDate.getTime()
      ) {
        return null;
      }

      const fullName = [personData.first_name, personData.last_name]
        .filter(Boolean)
        .join(" ");

      return {
        timestamp: touchpointTimestamp,
        person: {
          ...personData,
          displayInitials: (
            fullName ||
            personData.email_address ||
            touchpoint.person_id
          )
            .split(" ")
            .map((part) => part[0])
            .join("")
            .toUpperCase()
            .slice(0, 2),
          y: 0.5,
        },
        date: touchpoint.date,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .sort((a, b) => a.timestamp - b.timestamp);

  // Group touchpoints that are close to each other
  const groupedPoints: PersonScatterPoint[] = [];
  const DAYS_TO_COMBINE = customTimeRange ? 3 : 7; // Use 3 days for zoomed view, 7 for all time
  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  for (const touchpoint of filteredTouchpoints) {
    // Find the closest existing group within the time window
    const existingGroupIndex = groupedPoints.findIndex((group) => {
      const timeDiff = Math.abs(group.timestamp - touchpoint.timestamp);
      return timeDiff <= DAYS_TO_COMBINE * MS_PER_DAY;
    });

    if (existingGroupIndex >= 0) {
      // Add to existing group
      const group = groupedPoints[existingGroupIndex];
      group.people.push(touchpoint.person);

      // Update the timestamp to be the average of all points in the group
      const totalTimestamp = group.people.reduce(
        (sum: number, _: Person, index: number) => {
          return index === 0
            ? touchpoint.timestamp
            : sum + touchpoint.timestamp;
        },
        group.timestamp,
      );
      group.timestamp = Math.round(totalTimestamp / group.people.length);

      // Update display initials
      group.displayInitials = `+${group.people.length}`;
    } else {
      // Create new group
      groupedPoints.push({
        timestamp: touchpoint.timestamp,
        people: [touchpoint.person],
        displayInitials: touchpoint.person.displayInitials,
        y: 0.5,
      });
    }
  }

  return groupedPoints.sort((a, b) => a.timestamp - b.timestamp);
};

export const formatChartDomainAndTicks = (
  visibleTimeRange: {
    startDate: Date;
    endDate: Date;
    isCustomRange?: boolean;
    selectedRange?: {
      start: Date;
      end: Date;
    };
  } | null,
  viewportWidthPx: number = 1200,
) => {
  if (!visibleTimeRange)
    return { domain: [0, 0], ticks: [], tickFormatter: formatMonthYear };

  const { startDate, endDate, isCustomRange, selectedRange } = visibleTimeRange;

  // Always use full timeline for domain to enable scrolling
  const fullDomain = [startDate.getTime(), endDate.getTime()];

  // Default to month-year labels and base tick count
  let tickFormatter = formatMonthYear;
  const baseMaxTicks = BASE_MAX_TICKS; // base value for full-timeline view
  let windowSize: number | undefined;

  // --- Responsive max tick calculation ----------------------------------
  //   Use actual viewport width (or fallback) so labels cannot collide on
  //   narrow screens. 80 px per label keeps "Feb 2024" readable.
  let approxChartWidthPx = viewportWidthPx;

  // When user zooms into a custom range, the chart becomes scrollable and its
  // *rendered* width grows proportionally.  Approximate that extra width so we
  // can safely add more ticks.
  if (isCustomRange && selectedRange) {
    const fullDuration = endDate.getTime() - startDate.getTime();
    const selectedDuration =
      selectedRange.end.getTime() - selectedRange.start.getTime();
    if (selectedDuration > 0) {
      const ratio = fullDuration / selectedDuration; // how many screens the scroll area spans
      approxChartWidthPx = approxChartWidthPx * ratio * PADDING_FACTOR; // apply padding
    }
  }
  const widthLimitedMaxTicks = Math.max(
    4,
    Math.floor(approxChartWidthPx / MIN_TICK_GAP_PX),
  );
  let maxTicks = Math.min(baseMaxTicks, widthLimitedMaxTicks);

  // For custom ranges, adjust parameters based on selected range
  if (isCustomRange && selectedRange) {
    const selectedDuration =
      selectedRange.end.getTime() - selectedRange.start.getTime();
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const daysInSelectedRange = selectedDuration / MS_PER_DAY;

    // Allow up to 30 ticks for custom ranges, but still respect screen width
    const customBaseMax = CUSTOM_BASE_MAX_TICKS;
    maxTicks = Math.min(customBaseMax, widthLimitedMaxTicks);

    // Pass the visible window size
    windowSize = selectedDuration;

    // Use day-month format for shorter ranges
    if (daysInSelectedRange <= 30) {
      tickFormatter = formatDayMonth;
    }
  }

  // Generate ticks using the appropriate parameters
  let ticks = generateCustomTicks(
    startDate.getTime(),
    endDate.getTime(),
    maxTicks,
    windowSize,
  );

  // ------------------------------------------------------------------
  // Final safety pass: ensure labels cannot collide by enforcing a
  // minimum pixel gap between neighbouring ticks.
  // ------------------------------------------------------------------
  const pxPerMs = approxChartWidthPx / (fullDomain[1] - fullDomain[0] || 1);
  const filtered: number[] = [];
  let lastAcceptedX = -Infinity;
  for (const ts of ticks) {
    const x = (ts - fullDomain[0]) * pxPerMs;
    if (x - lastAcceptedX >= MIN_TICK_GAP_PX) {
      filtered.push(ts);
      lastAcceptedX = x;
    }
  }
  // Ensure at least two ticks (start & end) for context, avoiding duplicates
  if (filtered.length < 2 && ticks.length >= 2) {
    const firstTick = ticks[0];
    const lastTick = ticks[ticks.length - 1];

    if (!filtered.includes(firstTick)) {
      filtered.unshift(firstTick);
    }
    if (!filtered.includes(lastTick)) {
      filtered.push(lastTick);
    }
    // Guarantee chronological order after possible unshift / push
    filtered.sort((a, b) => a - b);
  }
  ticks = filtered;

  return {
    domain: fullDomain,
    ticks,
    tickFormatter,
  };
};

/**
 * Format milestone analysis for display on charts
 */
export function formatMilestoneAnalyses(
  MilestoneAnalyses: MilestoneAnalysis[] | undefined,
  visibleTimeRange: VisibleTimeRange | null,
) {
  if (!MilestoneAnalyses?.length || !visibleTimeRange) return [];

  // Filter out any undefined items before mapping
  return MilestoneAnalyses.filter((ma): ma is MilestoneAnalysis => !!ma).map(
    (ma) => {
      // Always use the opportunity created date for positioning in the minimap
      // This ensures alignment with the opportunity timeline
      const timestamp = new Date(
        ma.opportunity_created_date || ma.created,
      ).getTime();

      return {
        id: ma.analysis_id,
        opportunity_id: ma.opportunity_id,
        timestamp,
        moment_type: ma.analysis_type,
      };
    },
  );
}
